import { Box, Button, Divider, Typography } from "@mui/material";
import { Fragment, ReactNode } from "react";

import AppDialog from "components/common/AppDialog";

export type ChangeTone = "success" | "info" | "warning" | "error";

/** A headed list of changes ("Added", "Removed"...). */
export interface ChangeGroup {
  heading: string;
  tone: ChangeTone;
  items: ReactNode[];
}

export interface ChangeSection {
  label: string;
  /** Colours the label itself; grey when absent. */
  tone?: ChangeTone;
  /** A plain list right under the label. */
  items?: ReactNode[];
  groups?: ChangeGroup[];
  /** Rows of their own (a person with a line about them), divided. */
  rows?: ReactNode[];
}

/** The lists of a Client & User Setup change, by kind. */
export interface AccessChangeLists {
  label: string;
  items?: string[];
  added?: string[];
  updated?: string[];
  removed?: string[];
  enabled?: string[];
  disabled?: string[];
}

/** One section of the dialog from the lists of a Client & User Setup change. */
export function accessChangeSection(
  lists: AccessChangeLists,
  updatedTone: ChangeTone = "info",
): ChangeSection {
  const groups: ChangeGroup[] = [];
  const add = (heading: string, tone: ChangeTone, items?: string[]) => {
    if (items && items.length > 0) groups.push({ heading, tone, items });
  };
  add("Added", "success", lists.added);
  add("Updated", updatedTone, lists.updated);
  add("Enabled", "success", lists.enabled);
  add("Disabled", "error", lists.disabled);
  add("Removed", "error", lists.removed);
  return { label: lists.label, items: lists.items, groups };
}

function BulletList({ items }: { items: ReactNode[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
      {items.map((item, index) => (
        <Typography key={index} variant="body2" component="li">
          {item}
        </Typography>
      ))}
    </Box>
  );
}

interface ConfirmChangesDialogProps {
  open: boolean;
  title?: string;
  sections: ChangeSection[];
  /** Above the sections: a warning, or what is being changed. */
  intro?: ReactNode;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** "Nothing has been saved yet": the changes about to be saved, for a last look. */
export default function ConfirmChangesDialog({
  open,
  title = "Confirm Access Changes",
  sections,
  intro,
  confirmLabel = "Confirm",
  confirmDisabled = false,
  onCancel,
  onConfirm,
}: ConfirmChangesDialogProps) {
  return (
    <AppDialog
      open={open}
      onClose={onCancel}
      title={title}
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="contained"
            disabled={confirmDisabled}
            onClick={onConfirm}
            sx={{ fontWeight: 700 }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {intro}
      {sections.map((section, index) => (
        <Box key={section.label}>
          {index > 0 && <Divider sx={{ my: 2 }} />}
          <Typography
            variant="caption"
            color={section.tone ? `${section.tone}.main` : "text.secondary"}
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={0.6}
            display="block"
            sx={{ mb: 1 }}
          >
            {section.label}
          </Typography>
          {section.items && section.items.length > 0 && (
            <BulletList items={section.items} />
          )}
          {(section.groups ?? []).map((group, groupIndex, all) => (
            <Box
              key={group.heading}
              sx={{ mb: groupIndex < all.length - 1 ? 1.5 : 0 }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                letterSpacing={0.5}
                sx={{ color: `${group.tone}.main`, display: "block", mb: 0.5 }}
              >
                {group.heading}
              </Typography>
              <BulletList items={group.items} />
            </Box>
          ))}
          {(section.rows ?? []).map((row, rowIndex) => (
            <Fragment key={rowIndex}>
              <Divider />
              <Box sx={{ py: 1 }}>{row}</Box>
            </Fragment>
          ))}
        </Box>
      ))}
    </AppDialog>
  );
}
